import { useEffect, useRef, useState } from "react";
import { credibilityStats } from "@/mocks/credibility";
import ScrollReveal from "@/components/base/ScrollReveal";

function AnimatedCounter({ value, suffix, prefix = "" }: { value: string; suffix: string; prefix?: string }) {
  const [display, setDisplay] = useState("0");
  const ref = useRef<HTMLSpanElement>(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated.current) {
            hasAnimated.current = true;
            if (!isNaN(Number(value))) {
              const target = Number(value);
              let current = 0;
              const step = Math.max(1, Math.floor(target / 40));
              const timer = setInterval(() => {
                current += step;
                if (current >= target) {
                  current = target;
                  clearInterval(timer);
                }
                setDisplay(String(current));
              }, 30);
            } else {
              setDisplay(value);
            }
          }
        });
      },
      { threshold: 0.5 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [value]);

  return (
    <span ref={ref}>
      {prefix}{display}{suffix}
    </span>
  );
}

export default function Credibility() {
  return (
    <section id="credibilidade" className="relative w-full py-20 md:py-28 bg-organic-warm overflow-hidden">
      <div className="relative w-full px-4 sm:px-6 md:px-10 lg:px-16 max-w-7xl mx-auto">
        {/* Mission / About text */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 mb-20">
          <ScrollReveal>
            <span className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-primary-600 mb-4">
              Sobre a Big Mania
            </span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-heading font-bold text-foreground-950 leading-tight mb-6">
              Bem-vindo a{" "}
              <span className="text-primary-600">Big Mania</span>{" "}
              Alimentação
            </h2>
          </ScrollReveal>
          <ScrollReveal direction="up" delay={0.15}>
            <div className="flex flex-col justify-center">
              <p className="text-foreground-700 leading-relaxed text-base md:text-lg mb-4">
                A Big Mania transforma ingredientes selecionados em alimentos que unem sabor, qualidade e praticidade.
              </p>
              <p className="text-foreground-700 leading-relaxed text-base md:text-lg mb-4">
                Com produção própria e processos cuidadosamente desenvolvidos, oferecemos produtos frescos e congelados para consumidores, empresas, revendedores e eventos.
              </p>
              <p className="text-foreground-700 leading-relaxed text-base md:text-lg">
                Nosso compromisso está presente em cada etapa: da escolha dos ingredientes até a entrega do produto final, seguindo padrões que garantem frescor, segurança e excelência.
              </p>
            </div>
          </ScrollReveal>
        </div>

        {/* Stats */}
        <ScrollReveal direction="up" delay={0.1} staggerChildren staggerDelay={0.1}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-16">
            {credibilityStats.map((stat, idx) => (
              <div
                key={idx}
                className="relative p-6 md:p-8 rounded-2xl bg-background-50/80 border border-background-200/40 text-center group card-glow hover:border-primary-300/60 transition-all duration-500 hover:-translate-y-1"
              >
                <div className="text-3xl md:text-4xl font-heading font-bold text-primary-600 mb-2">
                  <AnimatedCounter value={stat.value} suffix={stat.suffix} prefix={(stat as any).prefix} />
                </div>
                <div className="text-sm text-foreground-600 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </ScrollReveal>

        {/* Company info */}
        <ScrollReveal direction="up" delay={0.15} staggerChildren staggerDelay={0.1}>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            <div className="p-6 rounded-2xl bg-background-50/80 border border-background-200/40 card-glow hover:border-primary-300/50 transition-all duration-500 hover:-translate-y-1">
              <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center mb-4">
                <i className="ri-building-2-line text-primary-600 text-lg"></i>
              </div>
              <h3 className="text-lg font-heading font-semibold text-foreground-950 mb-2">
                Indústria Alimentícia
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed">
                Referência em Fortaleza na fabricação de alimentos frescos e congelados. Produção própria com foco em qualidade e segurança alimentar.
              </p>
            </div>
            <div className="p-6 rounded-2xl bg-background-50/80 border border-background-200/40 card-glow hover:border-accent-300/50 transition-all duration-500 hover:-translate-y-1">
              <div className="w-10 h-10 rounded-xl bg-accent-100 flex items-center justify-center mb-4">
                <i className="ri-restaurant-line text-accent-600 text-lg"></i>
              </div>
              <h3 className="text-lg font-heading font-semibold text-foreground-950 mb-2">
                Portfólio
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed">
                Sanduíches naturais saudáveis, mini pastéis crocantes e soluções completas de alimentação para empresas e eventos. Qualidade que você pode confiar.
              </p>
            </div>
            <div className="p-6 rounded-2xl bg-background-50/80 border border-background-200/40 card-glow hover:border-secondary-300/50 transition-all duration-500 hover:-translate-y-1">
              <div className="w-10 h-10 rounded-xl bg-secondary-100 flex items-center justify-center mb-4">
                <i className="ri-calendar-event-line text-secondary-600 text-lg"></i>
              </div>
              <h3 className="text-lg font-heading font-semibold text-foreground-950 mb-2">
                Eventos
              </h3>
              <p className="text-sm text-foreground-600 leading-relaxed">
                Especialistas em alimentação para eventos corporativos e particulares. Cardápios personalizados com organização, pontualidade e excelente apresentação.
              </p>
            </div>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}